import { useCallback, useEffect, useReducer, type Dispatch } from "react";
import {
  CampaignServiceError,
  deleteCampaign as deleteCampaignApi,
  getCampaignHierarchy,
} from "../api";
import { CampaignHierarchyResponse } from "../types";
import { CampaignPageFilter } from "../types/campaign.ui";
import { CAMPAIGN_STATUSES } from "../types/campaign-status.constants";

type CampaignPageState = {
  data: CampaignHierarchyResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  filters: CampaignPageFilter;
};

type CampaignPageAction =
  | { type: "fetch/pending" }
  | { type: "fetch/fulfilled"; payload: CampaignHierarchyResponse }
  | { type: "fetch/rejected"; payload: string }
  | { type: "delete/fulfilled"; payload: string }
  | { type: "filter/searchChanged"; payload: string }
  | { type: "filter/statusChanged"; payload: CAMPAIGN_STATUSES | "all" };

const initialState: CampaignPageState = {
  data: null,
  isLoading: true,
  isError: false,
  error: null,
  filters: {
    searchTerm: "",
    statusFilter: "all",
  },
};

function reducer(
  state: CampaignPageState,
  action: CampaignPageAction,
): CampaignPageState {
  switch (action.type) {
    case "fetch/pending":
      return {
        ...state,
        isLoading: true,
        isError: false,
        error: null,
      };
    case "fetch/fulfilled":
      return {
        ...state,
        data: action.payload,
        isLoading: false,
        isError: false,
        error: null,
      };
    case "fetch/rejected":
      return {
        ...state,
        data: null,
        isLoading: false,
        isError: true,
        error: action.payload,
      };
    case "delete/fulfilled":
      return {
        ...state,
        data: state.data
          ? {
              campaigns: state.data.campaigns.filter(
                (campaign) => campaign.id !== action.payload,
              ),
            }
          : state.data,
      };
    case "filter/searchChanged":
      return {
        ...state,
        filters: {
          ...state.filters,
          searchTerm: action.payload,
        },
      };
    case "filter/statusChanged":
      return {
        ...state,
        filters: {
          ...state.filters,
          statusFilter: action.payload,
        },
      };
    default:
      return state;
  }
}

export interface UseCampaignPageStateResult {
  data: CampaignHierarchyResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  filters: CampaignPageFilter;
  dispatch: Dispatch<CampaignPageAction>;
  retry: () => Promise<void>;
  deleteCampaign: (campaignId: string) => Promise<void>;
}

export function useCampaignPageState(): UseCampaignPageStateResult {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchCampaignHierarchy = useCallback(async () => {
    dispatch({ type: "fetch/pending" });

    try {
      const response = await getCampaignHierarchy({
        searchTerm: state.filters.searchTerm,
        statusFilter: state.filters.statusFilter,
      });
      dispatch({ type: "fetch/fulfilled", payload: response });
    } catch (serviceError) {
      dispatch({
        type: "fetch/rejected",
        payload:
          serviceError instanceof CampaignServiceError
            ? serviceError.message
            : "Failed to load campaign hierarchy.",
      });
    }
  }, [state.filters.searchTerm, state.filters.statusFilter]);

  useEffect(() => {
    void fetchCampaignHierarchy();
  }, [fetchCampaignHierarchy]);

  const deleteCampaign = async (campaignId: string) => {
    await deleteCampaignApi(campaignId);
    dispatch({ type: "delete/fulfilled", payload: campaignId });
  };

  return {
    data: state.data,
    isLoading: state.isLoading,
    isError: state.isError,
    error: state.error,
    filters: state.filters,
    dispatch,
    retry: fetchCampaignHierarchy,
    deleteCampaign,
  };
}
